import * as React from "react";
import { AccountHolder, useAccount } from "../../Account";
import { UpdateDigitalLoansButton } from "./UpdateDigitalLoansButton";
import RenewLoansErrorDialog from "./RenewLoansErrorDialog";
import { HostSystemItemView } from "@tlc/ls2pac/dto/HostSystemItemView";
import axios from "axios";
import {
  Await,
  LoaderFunctionArgs,
  useLoaderData,
  useNavigation,
  useRevalidator,
  Navigation,
} from "react-router-dom";
import { getValidatedPage } from "./getValidatedPage";
import { isLoansSortType } from "./HostSystemItemSortControl";
import { GetCurrentLoansReturn } from "@tlc/ls2pac/dto/GetCurrentLoansReturn";
import { LoansViewData, SharedLoansView } from "./SharedLoansView";
import {
  RenewLoanInfo,
  RenewLoanResponseInfo,
} from "@tlc/ls2pac/dto/RenewLoanRequestInfo";
import { useToast } from "@tlc/ui/toast";
import { useLanguage } from "../../Language";
import {
  EResourceReturnResponse,
  EResourceService,
} from "@tlc/ls2pac/dto/ReturnLoan";
import { HostSystemItemSortType } from "@tlc/ls2pac/dto/HostSystemItemSortType";
import { LSResultItemListWithToolbarSkeleton } from "../search/ls/LSResultItem/LSResultItemListWithToolbarSkeleton";
import { useDialogRegistry } from "@tlc/ui/dialog/DialogRegistry";
import { Patron } from "@tlc/ls2pac/dto/Patron";
import { AccountSummary } from "@tlc/ls2pac/dto/AccountSummary";
import { useErrorDialog } from "@tlc/ui/dialog/error/ErrorDialogContext";
import { LoaderData } from "./MyAccount";
import {
  useLoansSelection,
  LoansSelectionProvider,
} from "./LoansSelectionProvider";

export interface BorrowerLoaderData extends LoansViewData {}

export async function BorrowerLoader({ params, request }: LoaderFunctionArgs) {
  const pageSize: number = 10;
  const searchParams = new URL(request.url).searchParams;
  const page = getValidatedPage(searchParams.get("page"));
  const sortParam = searchParams.get("sort");
  const sort =
    sortParam && isLoansSortType(sortParam)
      ? sortParam
      : HostSystemItemSortType.Status;
  const includeResources = true;

  const dataPromise = axios
    .get<GetCurrentLoansReturn>(
      `/loans/${
        page * pageSize - pageSize
      }/${pageSize}/${sort}/${includeResources}`,
    )
    .then(
      ({ data }): BorrowerLoaderData => ({
        page,
        sort,
        pageSize,
        totalLoans: data.totalLoans,
        totalLoansOverdue: data.totalLoansOverdue,
        loans: data.loans,
      }),
    )
    .catch((error) => {
      console.error("Error fetching loans", error);
      if (axios.isAxiosError(error) && error.response?.status === 403) {
        throw new Response("Forbidden", { status: 403 });
      }
      throw error;
    });

  return { data: dataPromise, pageSize };
}

interface BorrowerLoansContentProps extends BorrowerLoaderData {
  revalidate: () => void;
  navigation: Navigation;
  patron: Patron;
  accountSummary: AccountSummary;
  renewLoans: (
    renewLoanInfos: RenewLoanInfo[],
  ) => Promise<RenewLoanResponseInfo[]>;
  handleReturnEResource: (loan: HostSystemItemView) => Promise<void>;
  getPrintLoansData: (
    page: number,
    count: number,
    sortType: HostSystemItemSortType,
    includeResources?: boolean,
  ) => Promise<GetCurrentLoansReturn>;
}

const BorrowerLoansContent: React.FC<BorrowerLoansContentProps> = ({
  sort,
  page,
  pageSize,
  loans,
  totalLoans,
  totalLoansOverdue,
  revalidate,
  navigation,
  patron,
  accountSummary,
  renewLoans,
  handleReturnEResource,
  getPrintLoansData,
}) => {
  const { setSelectedIds } = useLoansSelection();
  const { showToast } = useToast();
  const { translate } = useLanguage();
  const { refreshAccountData } = useAccount();
  const { handleRequestError } = useErrorDialog();

  const [renewResponse, setRenewResponse] = React.useState<{
    failedRenewals: string[];
  } | null>(null);

  const showRenewDialog = React.useMemo(
    () => Boolean(renewResponse),
    [renewResponse],
  );

  const hadSuccessfulRenewals = React.useRef(0);

  const findHostSystemItemViewFromRenewLoanInfo = (
    renewLoanInfo: RenewLoanResponseInfo,
    loans: HostSystemItemView[],
  ) => {
    if (renewLoanInfo.service) {
      // dealing with a downloadable
      return loans.find(
        (loan) =>
          loan.eresourceId === renewLoanInfo.itemId &&
          loan.eresourceService === renewLoanInfo.service,
      );
    }
    return loans.find((loan) => loan.itemId === renewLoanInfo.itemId);
  };

  const handleRenewSelected = async (
    selectedResourceIds: readonly number[],
  ) => {
    if (selectedResourceIds.length === 0) return;
    try {
      const renewLoanInfos = loans
        .filter(
          (loan) =>
            loan.resource && selectedResourceIds.includes(loan.resource.id),
        )
        .map((loan) => {
          return {
            itemId: loan.downloadable ? loan.eresourceId! : loan.itemId!,
            downloadable: loan.downloadable,
            service: loan.downloadable ? loan.eresourceService : "", //prefer string to undefined
          };
        });

      const results = await renewLoans(renewLoanInfos);
      const failed = results.filter((r) => !r.success);
      const succeeded = results.filter((r) => r.success);

      const failedResourceIds = failed
        .map(
          (f) =>
            findHostSystemItemViewFromRenewLoanInfo(f, loans)?.resource?.id,
        )
        .filter((id): id is number => id !== undefined);

      if (failed.length > 0) {
        const failedTitles = failed.map(
          (f) =>
            findHostSystemItemViewFromRenewLoanInfo(f, loans)?.title ||
            f.itemId,
        );
        setRenewResponse({ failedRenewals: failedTitles });
      }

      if (succeeded.length > 0) {
        await AccountHolder.fetchAccountData(true);
        setSelectedIds(failedResourceIds);

        hadSuccessfulRenewals.current = succeeded.length;

        if (failed.length === 0) {
          showToast(
            `${succeeded.length} ${translate("loanRenewSuccess")}`,
            "success",
          );
          hadSuccessfulRenewals.current = 0;
        }
      }

      revalidate();
    } catch (error: unknown) {
      // No expected exceptions
      // Handle Network and other unexpected errors here Bad Gateway, etc.
      handleRequestError(error);
    }
  };

  const handleRenewDialogClose = () => {
    setRenewResponse(null);
    if (hadSuccessfulRenewals.current > 0) {
      showToast(
        `${hadSuccessfulRenewals.current} ${translate("loanRenewSuccess")}`,
        "success",
      );
      hadSuccessfulRenewals.current = 0;
    }
  };

  const { registerDialog } = useDialogRegistry();
  React.useEffect(() => {
    return registerDialog(() => setRenewResponse(null));
  }, [registerDialog, setRenewResponse]);

  return (
    <>
      <SharedLoansView
        sort={sort}
        page={page}
        pageSize={pageSize}
        loans={loans}
        totalLoans={totalLoans}
        totalLoansOverdue={totalLoansOverdue}
        displayedLoan={loans}
        requiresDateRange={false}
        fetchPrintData={async () => {
          const response = await getPrintLoansData(0, totalLoans, sort, false);
          return response.loans;
        }}
        onRenewSelected={handleRenewSelected}
        onReturnEResource={handleReturnEResource}
        isLoading={navigation.state === "loading"}
        toolbarActions={
          <UpdateDigitalLoansButton
            patronId={patron.patronId}
            onUpdate={() => {
              refreshAccountData();
            }}
            lastUpdated={accountSummary.digitalInformationLastUpdated}
          />
        }
        isStudent={false}
        patronId={patron?.patronId}
        digitalInformationLastUpdated={
          accountSummary?.digitalInformationLastUpdated
        }
      />
      <RenewLoansErrorDialog
        open={showRenewDialog}
        onClose={handleRenewDialogClose}
        failedRenewals={renewResponse?.failedRenewals ?? []}
      />
    </>
  );
};

export const BorrowerLoansView: React.FC = () => {
  const { accountSummary, patron, refreshAccountData } = useAccount();

  const { data, pageSize } = useLoaderData() as LoaderData<
    typeof BorrowerLoader
  >;

  const navigation = useNavigation();
  const revalidator = useRevalidator();
  const { showToast } = useToast();
  const { translate, translateToken } = useLanguage();
  const { handleRequestError } = useErrorDialog();

  const renewLoans = async (
    renewLoanInfos: RenewLoanInfo[],
  ): Promise<RenewLoanResponseInfo[]> => {
    const { data } = await axios.post<{
      renewLoanInfos: RenewLoanResponseInfo[];
    }>("/loans/renew", { renewLoanInfos });
    return data.renewLoanInfos.map((result) => ({
      ...result,
      date: new Date(result.date),
    }));
  };

  const handleReturnEResource = async (loan: HostSystemItemView) => {
    try {
      const { data } = await axios.post<EResourceReturnResponse>(
        "/eResource/return",
        {
          titleId: loan.eresourceId,
          service: loan.eresourceService,
          patronId: patron.patronId,
        },
      );
      if (data.success) {
        showToast(translate("eResourceLoanReturned"), "success");
        await AccountHolder.fetchAccountData(true);
        refreshAccountData();
      } else {
        showToast(translateToken(data.message), "error");
      }
    } catch (e) {
      handleRequestError(e);
    }
    revalidator.revalidate();
  };

  const getPrintLoansData = async (
    page: number = 0,
    count: number,
    sortType: HostSystemItemSortType,
    includeResources = false,
  ): Promise<GetCurrentLoansReturn> => {
    const start = page * count;
    const response = await axios.get<GetCurrentLoansReturn>(
      `/loans/${start}/${count}/${sortType}/${includeResources}`,
    );
    return response.data;
  };

  return (
    <React.Suspense
      fallback={
        <LSResultItemListWithToolbarSkeleton
          pageSize={pageSize}
          hideCheckbox={false}
          hideActions={false}
        />
      }
    >
      <Await resolve={data}>
        {({
          sort,
          page,
          pageSize,
          loans,
          totalLoans,
          totalLoansOverdue,
        }: BorrowerLoaderData) => {
          const items = loans.filter((loan) => !!loan.resource);

          const isSelectable = (loan: HostSystemItemView): boolean => {
            return (
              !!loan.resource &&
              loan.renewable &&
              (!loan.downloadable ||
                loan.eresourceService === EResourceService.mackin)
            );
          };

          return (
            <LoansSelectionProvider
              items={items}
              getId={(loan) => loan.resource!.id}
              isSelectable={isSelectable}
            >
              <BorrowerLoansContent
                sort={sort}
                page={page}
                pageSize={pageSize}
                loans={loans}
                totalLoans={totalLoans}
                totalLoansOverdue={totalLoansOverdue}
                revalidate={revalidator.revalidate}
                navigation={navigation}
                patron={patron}
                accountSummary={accountSummary}
                renewLoans={renewLoans}
                handleReturnEResource={handleReturnEResource}
                getPrintLoansData={getPrintLoansData}
              />
            </LoansSelectionProvider>
          );
        }}
      </Await>
    </React.Suspense>
  );
};
